import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import MessageModal from '@/components/ui/MessageModal';
import LoadingButton from '@/components/ui/LoadingButton';
import StatusModal from '@/components/ui/StatusModal';
import Axios from '@/shared/api/axios';

// 通知項目定義，key 對應 API 欄位
const NOTIFY_ITEMS = [
  { key: 'subscription', labelKey: 'notify_subscription' },
  { key: 'earning', labelKey: 'notify_earning' },
  { key: 'wallet', labelKey: 'notify_wallet' },
  { key: 'chat_message', labelKey: 'notify_chat_message' },
  { key: 'chat_digest', labelKey: 'notify_chat_digest' },
];

function NotificationSettings() {
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const successModalRef = useRef();
  const statusModalRef = useRef();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSettingChange = (key, checked) => {
    setSettings((prev) => ({ ...prev, [key]: checked }));
    setHasChanges(true);
  };

  useEffect(() => {
    fetchNotifySetting();
  }, []);

  // 取得通知設定 (1-11)
  const fetchNotifySetting = async () => {
    setLoading(true);
    try {
      const response = await Axios.get('/user/get-notify-setting');

      if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
        setSettings(response.data.data);
        setHasChanges(false);
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error('Failed to fetch notify setting:', error);
      // NETWORK_ERROR 已由 axios 攔截器觸發全域錯誤畫面，不再重複彈窗
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setLoading(false);
    }
  };

  // 更新通知設定 (1-12)
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await Axios.post('/user/update-notify-setting', {
        subscription: settings.subscription,
        earning: settings.earning,
        wallet: settings.wallet,
        chat_message: settings.chat_message,
        chat_digest: settings.chat_digest,
      });

      if (response.data?.status_code === 'SYSTEM_1000') {
        setHasChanges(false);
        successModalRef.current?.show();
      } else if (response.data?.status_code) {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error('Failed to update notify setting:', error);
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <h3 className="mb-4">{t('notify_settings')}</h3>

      <div className="bg-white rounded p-4 mb-4">
        <h5 className="mb-4">{t('email_notify')}</h5>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : settings && (
          <>
            {NOTIFY_ITEMS.map(({ key, labelKey }) => (
              <div key={key} className="mb-3">
                <Form.Check
                  type="switch"
                  id={`notify-${key}-switch`}
                  label={t(labelKey)}
                  checked={settings[key]}
                  onChange={(e) => handleSettingChange(key, e.target.checked)}
                />
              </div>
            ))}

            <div className="d-flex justify-content-end">
              <LoadingButton
                className="btn btn-primary rounded-pill px-4"
                onClick={handleSave}
                disabled={!hasChanges}
                isLoading={isSaving}
              >
                {t('save')}
              </LoadingButton>
            </div>
          </>
        )}
      </div>

      {/* 成功訊息 Modal */}
      <MessageModal ref={successModalRef} status="success">
        {t('saved_successfully')}
      </MessageModal>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
}

export default NotificationSettings;
